/**
 * useEditAppliedStatus — drives the inline "Edit applied · Undo" status a VQE
 * surface (full-tab header, compact popover footer) shows after one of ITS OWN
 * edits lands in the buffer.
 *
 * Why a surface-local status rather than a toast: the app-root toast floats
 * bottom-right, exactly over the results grid the edit just refreshed, and it
 * cannot tell which surface made the edit. The status sits in the committing
 * surface's own chrome, so it never covers the grid.
 *
 * Shown when `vqeStore.commitFeedback` gets a NEW success payload whose
 * `documentId` is this surface's document; payloads that already existed when
 * the surface mounted are ignored. Hidden again when:
 *  - `STATUS_VISIBLE_MS` elapses,
 *  - the buffer moves away from the post-edit text (Cmd/Ctrl+Z, typing, the
 *    next edit — a next edit re-shows it),
 *  - `dismiss()` is called (the Undo button).
 *
 * `readSource` returns the surface's live buffer right after the commit (the
 * compact popover's Monaco value is current before the tab store catches up),
 * and `query` is the buffer the surface re-renders with. The status only hides
 * on a `query` change once `query` has caught up with the post-edit text, so
 * the compact popover's debounced store sync never hides it early.
 */
import { useCallback, useEffect, useRef, useState } from 'react';

import { useVqeStore } from '../store/vqeStore';
import type { VqeCommitFeedback } from '../store/vqeStore';

export const STATUS_VISIBLE_MS = 8000;

export type UseEditAppliedStatusArgs = {
  /** This surface's SR-Edit document id; `null` while it has none. */
  readonly documentId: string | null | undefined;
  /** The buffer this surface re-renders with. */
  readonly query: string;
  /** The live buffer right now (read once, when an edit lands). */
  readonly readSource: () => string;
};

type StatusState = {
  readonly visible: boolean;
  /** `commitFeedback.at` of the edit being shown — one token per edit. */
  readonly at: number;
  /** Post-edit buffer text. */
  readonly source: string;
  /** `query` has matched `source` at least once since the edit. */
  readonly synced: boolean;
};

const HIDDEN: StatusState = { visible: false, at: 0, source: '', synced: false };

export function useEditAppliedStatus({ documentId, query, readSource }: UseEditAppliedStatusArgs) {
  const commitFeedback = useVqeStore((s) => s.commitFeedback);
  const [state, setState] = useState<StatusState>(HIDDEN);
  // Seeded with the payload present at mount, so an edit made before this
  // surface existed never shows up here.
  const seenRef = useRef<VqeCommitFeedback | null>(commitFeedback);
  const readSourceRef = useRef(readSource);
  readSourceRef.current = readSource;

  useEffect(() => {
    if (!commitFeedback || commitFeedback === seenRef.current) return;
    seenRef.current = commitFeedback;
    if (commitFeedback.kind !== 'success' || !documentId || commitFeedback.documentId !== documentId) return;
    const source = readSourceRef.current();
    setState({ visible: true, at: commitFeedback.at, source, synced: source === query });
    // `query` is read for the initial `synced` flag only; the effect below
    // tracks later changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commitFeedback, documentId]);

  useEffect(() => {
    if (!state.visible) return;
    if (query === state.source) {
      if (!state.synced) setState((prev) => ({ ...prev, synced: true }));
      return;
    }
    if (state.synced) setState(HIDDEN);
  }, [query, state]);

  useEffect(() => {
    if (!state.visible) return undefined;
    const timer = setTimeout(() => setState(HIDDEN), STATUS_VISIBLE_MS);
    return () => clearTimeout(timer);
    // Restart the timer for each new edit (a new post-edit `source`).
  }, [state.visible, state.source]);

  useEffect(() => {
    setState(HIDDEN);
  }, [documentId]);

  const dismiss = useCallback(() => setState(HIDDEN), []);

  // `editToken` changes once per shown edit — the edit highlight re-marks on it.
  return { visible: state.visible, dismiss, editToken: state.visible ? state.at : null };
}
